import GroupResource from '@/api/resources/group'
import { IGroup } from '@/interfaces/entities/group'
import { IGroupReport } from '@/interfaces/stores/report'
import { formatPeriodDate } from '@/utils/date/date-time'
import {
  countCorrelatedCharts,
  type ICorrelatedChartsPresent,
} from '@/utils/report/correlated-charts-layout'
import { defineStore } from 'pinia'
import { IShift } from '@/interfaces/entities/shifts'

function resolveCorrelatedChartsPresent(report: IGroupReport | undefined | null): ICorrelatedChartsPresent {
  return {
    demand: (report?.energy_sensors_readings?.length ?? 0) > 0,
    temperature: (report?.temperature_sensors_readings?.length ?? 0) > 0,
    door: (report?.door_open_readings?.length ?? 0) > 0,
  }
}

export const useReportStore = defineStore({
  id: 'report',
  persist: true,

  state: () => ({
    report: {} as IGroupReport,
    compareReport: {} as IGroupReport,
    currentGroup: {} as IGroup,
    baseChartColor: '#7F7FFF',
    basePeriod: [] as Date[],
    baseShift: null as IShift | null,
    compareGroup: {} as IGroup,
    comparePeriod: [] as Date[],
    compareShift: null as IShift | null,
    compareChartColor: '#26DF20',
    showAlarmReportBase: false as boolean,
    showAlarmReportCompare: false as boolean,
  }),

  getters: {
    getReport: (state) => state.report,
    hasReport: (state) => {
      const hasEnergyData = state.report.energy_sensors_readings?.length > 0
      const hasTemperatureData = state.report.temperature_sensors_readings?.length > 0
      const hasOpenData = state.report.door_open_readings?.length > 0

      return hasEnergyData || hasTemperatureData || hasOpenData
    },

    hasEnergyData: (state) => {
      return state.report.energy_sensors_readings?.length > 0
    },

    getBaseChartColors: (state) => state.baseChartColor,
    getBaseGroupFilter: (state) => state.currentGroup,
    getBasePeriodFilter: (state) => state.basePeriod,

    getCompareChartColors: (state) => state.compareChartColor,
    getComparePeriodFilter: (state) => state.comparePeriod,
    getCompareGroupFilter: (state) => state.compareGroup,
    getBaseEnergySensorReadings: (state) => {
      return state.report.energy_sensors_readings?.map((sensor) => ({
        sensorName: sensor.sensor_name,
        readings: sensor.readings,
      }))
    },
    getCompareEnergySensorReadings: (state) => {
      return state.compareReport?.energy_sensors_readings?.map((sensor) => ({
        sensorName: sensor.sensor_name,
        readings: sensor.readings,
      }))
    },
    getBaseTemperatureReadings: (state) => {
      return state.report.temperature_sensors_readings?.map((sensor) => ({
        sensorName: sensor.sensor_name,
        readings: sensor.readings,
      }))
    },
    getCompareTemperatureReadings: (state) => {
      return state.compareReport?.temperature_sensors_readings?.map((sensor) => ({
        sensorName: sensor.sensor_name,
        readings: sensor.readings,
      }))
    },

    hasTemperature: (state) => state.report.temperature_sensors_readings?.length > 0,
    hasCompareTemperature: (state) => state.compareReport?.temperature_sensors_readings?.length > 0,

    correlatedChartsPresentBase: (state): ICorrelatedChartsPresent =>
      resolveCorrelatedChartsPresent(state.report),

    correlatedChartsPresentCompare: (state): ICorrelatedChartsPresent =>
      resolveCorrelatedChartsPresent(state.compareReport),

    hasCorrelatedChartsBase: (state): boolean =>
      countCorrelatedCharts(resolveCorrelatedChartsPresent(state.report)) >= 1,

    hasCorrelatedChartsCompare: (state): boolean =>
      countCorrelatedCharts(resolveCorrelatedChartsPresent(state.compareReport)) >= 1,

    correlatedChartsCountBase: (state): number =>
      countCorrelatedCharts(resolveCorrelatedChartsPresent(state.report)),

    correlatedChartsCountCompare: (state): number =>
      countCorrelatedCharts(resolveCorrelatedChartsPresent(state.compareReport)),

    //Abertura de porta
    hasDoorOpen: (state) => state.report?.door_open_readings?.length > 0,
    doorOpenReadings: (state) => {
      return state.report?.door_open_readings || []
    },
    hasCompareDoorOpen: (state) => state.compareReport?.door_open_readings?.length > 0,
    compareDoorOpenReadings: (state) => {
      return state.compareReport?.door_open_readings || []
    },

    hasCompareEnergySensorReadings: (state) => state.compareReport?.energy_sensors_readings?.length > 0,

    getCompareEnergySensorConsumption: (state) => {
      return state.compareReport.energy_sensors_totalizers?.map((totalizer) => ({
        sensorName: totalizer.sensor_name,
        totalConsumption: totalizer.totalizer.consumption.total_consumption_kwh,
      }))
    },
    getCompareEnergySensorDemand: (state) => {
      return state.compareReport.energy_sensors_totalizers?.map((totalizer) => ({
        sensorName: totalizer.sensor_name,
        totalDemand: totalizer.totalizer.demand.off_peak_demand_kw,
      }))
    },
    getCompareEnergySensorUtilization: (state) => {
      return state.compareReport.energy_sensors_totalizers?.map((totalizer) => ({
        sensorName: totalizer.sensor_name,
        totalUtilization: totalizer.totalizer.utilization.utilization,
        totalUtilizationHours: totalizer.totalizer.utilization.total_utilization_time / 60,
        metaPercentage: state.compareReport.utilization_percentage_setpoint,
        metaHours: state.compareReport.utilization_hours_setpoint,
      }))
    },
    getCompareAmountEnergySensorUtilizationHours: (state) => {
      const totalUtilizationHoursSum =
        state.compareReport.energy_sensors_totalizers?.reduce((sum, totalizer) => {
          return sum + (totalizer.totalizer?.utilization?.total_utilization_time || 0)
        }, 0) || 0

      const count = state.compareReport.energy_sensors_totalizers?.length || 1

      return {
        totalUtilizationHours: totalUtilizationHoursSum / 60,
        metaHours: state.compareReport.utilization_hours_setpoint * count || 0,
      }
    },
    getCompareAmountEnergySensorUtilizationPercentage: (state) => {
      const totalUtilization =
        state.compareReport.energy_sensors_totalizers?.reduce((sum, totalizer) => {
          return sum + (totalizer.totalizer?.utilization?.total_utilization_time || 0)
        }, 0) || 0

      const accumulatedMinutes =
        state.compareReport.energy_sensors_totalizers?.reduce((sum, totalizer) => {
          return sum + (totalizer.totalizer?.utilization?.accumulated_minutes_total || 0)
        }, 0) || 0

      return {
        totalUtilizationPercentage: (totalUtilization / accumulatedMinutes) * 100 || 0,
        metaPercentage: state.compareReport.utilization_percentage_setpoint || 0,
      }
    },

    utizationHourSetpoint: (state) => state.report,

    getEnergySensorConsumption: (state) => {
      return state.report.energy_sensors_totalizers?.map((totalizer) => ({
        sensorName: totalizer.sensor_name,
        totalConsumption: totalizer.totalizer.consumption.total_consumption_kwh,
      }))
    },

    getEnergySensorDemand: (state) => {
      return state.report.energy_sensors_totalizers?.map((totalizer) => ({
        sensorName: totalizer.sensor_name,
        totalDemand: totalizer.totalizer.demand.off_peak_demand_kw,
      }))
    },

    getEnergySensorUtilization: (state) => {
      return state.report.energy_sensors_totalizers?.map((totalizer) => ({
        sensorName: totalizer.sensor_name,
        totalUtilization: totalizer.totalizer.utilization.utilization,
        totalUtilizationHours: totalizer.totalizer.utilization.total_utilization_time / 60,
        metaPercentage: state.report.utilization_percentage_setpoint,
        metaHours: state.report.utilization_hours_setpoint,
      }))
    },

    getAmountEnergySensorUtilizationHours: (state) => {
      const totalUtilizationHoursSum =
        state.report.energy_sensors_totalizers?.reduce((sum, totalizer) => {
          return sum + (totalizer.totalizer?.utilization?.total_utilization_time || 0)
        }, 0) || 0
      const count = state.report.energy_sensors_totalizers?.length || 1

      return {
        totalUtilizationHours: totalUtilizationHoursSum / 60,
        metaHours: state.report.utilization_hours_setpoint * count || 0,
      }
    },

    getAmountEnergySensorUtilizationPercentage: (state) => {
      const totalUtilization =
        state.report.energy_sensors_totalizers?.reduce((sum, totalizer) => {
          return sum + (totalizer.totalizer?.utilization?.total_utilization_time || 0)
        }, 0) || 0

      const accumulatedMinutes =
        state.report.energy_sensors_totalizers?.reduce((sum, totalizer) => {
          return sum + (totalizer.totalizer?.utilization?.accumulated_minutes_total || 0)
        }, 0) || 0

      return {
        totalUtilizationPercentage: (totalUtilization / accumulatedMinutes) * 100,
        metaPercentage: state.report.utilization_percentage_setpoint || 0,
      }
    },
  },

  actions: {
    async fetchBaseReport() {
      const query = {
        groupId: this.currentGroup.id,
        dateRange: formatPeriodDate(this.basePeriod),
        shiftId: this.baseShift?.id || null,
      }

      await GroupResource.getReport(query).then((response) => {
        this.report = response
      })
    },

    async fetchCompareReport() {
      const query = {
        groupId: this.compareGroup.id,
        dateRange: formatPeriodDate(this.comparePeriod),
        shiftId: this.compareShift?.id || null,
      }

      await GroupResource.getReport(query).then((response) => {
        this.compareReport = response
      })
    },

    removeCompareReport() {
      this.compareReport = {} as IGroupReport
    },

    setBaseGroup(group: IGroup) {
      this.currentGroup = group
    },

    setBasePeriod(period: Date[]) {
      this.basePeriod = period
    },

    setBaseShift(shift: IShift | null) {
      this.baseShift = shift
    },

    setBaseChartColor(color: string) {
      this.baseChartColor = color
    },

    setCompareGroup(group: IGroup) {
      this.compareGroup = group
    },

    setCompareShift(shift: IShift | null) {
      this.compareShift = shift
    },

    setCompareChartColor(color: string) {
      this.compareChartColor = color
    },

    setComparePeriod(period: Date[]) {
      this.comparePeriod = period
    },

    setShowAlarmReportBase(value: boolean) {
      this.showAlarmReportBase = value
    },

    setShowAlarmReportCompare(value: boolean) {
      this.showAlarmReportCompare = value
    },
  },
})
